import React, { memo, useCallback, useMemo } from 'react';
import { BsCircleFill } from 'react-icons/bs';
import {
  Cell,
  Legend,
  Pie,
  PieChart,
  ResponsiveContainer,
  Tooltip,
} from 'recharts';
import { Props } from 'recharts/types/component/DefaultLegendContent';

import { ItemStatistics } from './ItemStatistics';

import icBest from '@/assets/ic_better-score.svg';
import icPie from '@/assets/ic_pie.svg';
import icWorse from '@/assets/ic_worse-score.svg';
import { Card, Divider } from '@/components';
import useAuth from '@/hooks/useAuth';
import { useIsDesktop } from '@/hooks/useIsDesktop';
import { RecordsTda, StatiticsTda } from '@/types/tda';
import { cn, COLORS } from '@/utils';

const cardClasses = 'lg:flex-1 w-full bg-white p-0 pb-5 mt-4 pt-[1px] lg:mt-0';

// const RADIAN = Math.PI / 180;

const COLORS_PIE = [
  COLORS['gdt-blue'].dark,
  COLORS['gdt-blue'].header,
  COLORS['gdt-blue'].DEFAULT,
  COLORS['gdt-blue'].light,
];

interface DataPie {
  name: string;
  value: number;
}
const RecordCards: React.FC<{ data?: RecordsTda }> = memo(({ data }) => {
  const { isDesktop } = useIsDesktop();
  const { userInitialData: userData } = useAuth();

  const nameMatchDay = useCallback(
    (order: number): string => {
      return (
        userData?.todasLasFechas.find((date) => date.orden === order)?.nombre ||
        ''
      );
    },
    [userData?.todasLasFechas]
  );

  if (!data) {
    return;
  }

  return (
    <div className="lg:mt-9">
      <p className="font-bold hidden lg:block">Récords del Torneo</p>
      <p className="font-bold lg:hidden">Récords de los mejores</p>
      <Card
        className={cn(`lg:rounded-bl-none lg:rounded-br-none ${cardClasses}`, {
          'lg:mt-4': isDesktop,
        })}
      >
        <ItemStatistics
          className="px-4"
          name={data?.mejor_ptj_tda?.nombreMostrar || '-'}
          score={data?.mejor_ptj_tda?.puntaje}
          sourcIcon={icBest}
          title="Mejor puntaje del torneo"
          tourney={nameMatchDay(data?.mejor_ptj_tda?.ordenFecha) || '-'}
          variant="green"
        />
        <Divider className="mt-5" type="horizontal" />
        <ItemStatistics
          className="px-4"
          name={data?.mejor_ptj_historico?.nombreMostrar || '-'}
          score={data?.mejor_ptj_historico?.puntaje}
          sourcIcon={icBest}
          title="Mejor puntaje histórico Gran DT"
          tourney={
            (data?.mejor_ptj_historico_nombreEdicion || '') +
            ' - ' +
            (nameMatchDay(data?.mejor_ptj_historico?.ordenFecha) || '-')
          }
          variant="green"
        />
      </Card>
      {isDesktop && <Divider type="horizontal" />}
      <p className="font-bold mt-6 lg:hidden lg:mt-0">Récords de los peores</p>
      <Card
        className={cn('lg:rounded-tl-none lg:rounded-tr-none', cardClasses)}
      >
        <ItemStatistics
          className="px-4"
          name={data?.peor_ptj_tda?.nombreMostrar || '-'}
          score={data?.peor_ptj_tda?.puntaje}
          sourcIcon={icWorse}
          title="Peor puntaje del torneo"
          tourney={nameMatchDay(data?.peor_ptj_tda?.ordenFecha) || '-'}
          variant="red"
        />
        <Divider className="mt-5" type="horizontal" />
        <ItemStatistics
          className="px-4"
          name={data?.peor_ptj_historico?.nombreMostrar || '-'}
          score={data?.peor_ptj_historico?.puntaje}
          sourcIcon={icWorse}
          title="Peor puntaje histórico Gran DT"
          tourney={
            (data?.peor_ptj_historico_nombreEdicion || '') +
            ' - ' +
            (nameMatchDay(data?.peor_ptj_historico?.ordenFecha) || '-')
          }
          variant="red"
        />
      </Card>
    </div>
  );
});

const GraphicCard: React.FC<{ data?: StatiticsTda }> = memo(({ data }) => {
  const { isDesktop } = useIsDesktop();

  const dataByClub = useMemo(() => {
    return (
      data &&
      data?.jugadoresPorClub?.map((item) => {
        return { name: item.nombre, value: item.porcentaje } as DataPie;
      })
    );
  }, [data]);

  const dataByFans = useMemo(() => {
    return (
      data &&
      data?.hinchasClub?.map((item) => {
        return { name: item.nombre, value: item.porcentaje } as DataPie;
      })
    );
  }, [data]);

  // se elimina con el DTA-1323
  // const renderCustomizedLabel = useCallback(
  //   ({
  //     cx,
  //     cy,
  //     midAngle,
  //     innerRadius,
  //     outerRadius,
  //     value,
  //   }: {
  //     cx: number;
  //     cy: number;
  //     midAngle: number;
  //     innerRadius: number;
  //     outerRadius: number;
  //     value: number;
  //   }) => {
  //     const radius = innerRadius + (outerRadius - innerRadius) * 0.7;
  //     const x = cx + radius * Math.cos(-midAngle * RADIAN);
  //     const y = cy + radius * Math.sin(-midAngle * RADIAN);

  //     const formatedValue =
  //       isNaN(parseInt(String(value).split('.')[1])) ||
  //       parseInt(String(value.toFixed(4)).split('.')[1]) >= 9500 ||
  //       parseInt(String(value.toFixed(4)).split('.')[1]) <= 1500
  //         ? value.toFixed(0)
  //         : value.toFixed(2);

  //     return (
  //       <text
  //         dominantBaseline="central"
  //         fill="white"
  //         textAnchor="middle"
  //         x={x}
  //         y={y}
  //       >
  //         {`${formatedValue}%`}
  //       </text>
  //     );
  //   },
  //   []
  // );

  const CustomTooltip = useCallback(
    ({ active, payload }: { active: boolean; payload: DataPie[] }) => {
      if (active && payload && payload.length) {
        const value =
          isNaN(parseInt(String(payload[0].value).split('.')[1])) ||
          parseInt(String(payload[0].value.toFixed(4)).split('.')[1]) >= 9500 ||
          parseInt(String(payload[0].value.toFixed(4)).split('.')[1]) <= 1500
            ? payload[0].value.toFixed(0)
            : payload[0].value.toFixed(2);

        return (
          <div className="bg-white p-2 rounded-lg shadow-lg">
            <p>{`${payload[0].name}: ${value}%`}</p>
          </div>
        );
      }

      return null;
    },
    []
  );

  const renderLegend = useCallback(({ payload }: Props) => {
    return (
      <ul className="w-full px-2">
        {payload?.map((entry, index) => (
          <li
            key={`item-${index}`}
            className="inline-block align-middle space-x-2 mr-4 text-xs"
          >
            <BsCircleFill
              className="inline-block align-middle"
              color={`${COLORS_PIE[index % COLORS_PIE.length]}`}
              size={9}
            />
            <p className="inline-block align-middle">{entry.value}</p>
          </li>
        ))}
      </ul>
    );
  }, []);

  const titleGraphics = useCallback((title: string) => {
    return (
      <p className="text-sm mt-5 px-6 font-bold text-gdt-gray-2 lg:text-gdt-gray lg:px-0 lg:mb-4">
        {title}
      </p>
    );
  }, []);

  return (
    <div className="lg:flex lg:flex-wrap">
      {/* GRAFICOS DE TORTA */}
      <div className="mt-6 lg:mt-8 w-full lg:flex lg:items-center lg:space-x-2">
        {isDesktop && (
          <figure>
            <img alt="Estadísticas del Torneo de Amigos" src={icPie} />
          </figure>
        )}
        <p className="font-bold lg:text-base">
          Estadísticas del Torneo de Amigos
        </p>
      </div>
      <div className="flex-1">
        {isDesktop && titleGraphics('Futbolistas elegidos por club')}
        <Card
          className={cn('lg:rounded-tr-none lg:rounded-br-none', cardClasses)}
        >
          {!isDesktop && titleGraphics('Futbolistas elegidos por club')}
          <ResponsiveContainer height={240} width="100%">
            <PieChart key={dataByClub?.join('-')}>
              <Pie
                key={dataByClub?.join()}
                className="outline-none"
                data={dataByClub}
                dataKey="value"
                // label={renderCustomizedLabel} eliminado con el DTA-1323
                labelLine={false}
              >
                {dataByClub?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}-club`}
                    fill={COLORS_PIE[index % COLORS_PIE.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip active={false} payload={[]} />}
              />
              <Legend content={renderLegend} iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
      {isDesktop && <Divider className="mt-5" type="vertical" />}
      <div className="flex-1 ml-2">
        {isDesktop && titleGraphics('Hinchas por club')}
        <Card
          className={cn('lg:rounded-tl-none lg:rounded-bl-none', cardClasses)}
        >
          {!isDesktop && titleGraphics('Hinchas por club')}
          <ResponsiveContainer height={240} width="100%">
            <PieChart key={dataByFans?.join('-')}>
              <Pie
                key={dataByFans?.join()}
                className="outline-none"
                data={dataByFans}
                dataKey="value"
                // label={renderCustomizedLabel} eliminado con el DTA-1323
                labelLine={false}
              >
                {dataByFans?.map((entry, index) => (
                  <Cell
                    key={`cell-${index}-fans`}
                    fill={COLORS_PIE[index % COLORS_PIE.length]}
                  />
                ))}
              </Pie>
              <Tooltip
                content={<CustomTooltip active={false} payload={[]} />}
              />
              <Legend content={renderLegend} iconType="circle" />
            </PieChart>
          </ResponsiveContainer>
        </Card>
      </div>
    </div>
  );
});

const WrapperMobile: React.FC<{
  dataRecords?: RecordsTda;
  dataStatitics?: StatiticsTda;
}> = memo(({ dataRecords, dataStatitics }) => {
  return (
    <div className="flex flex-col p-6 lg:flex-row">
      {dataRecords && <RecordCards data={dataRecords} />}
      {dataStatitics && <GraphicCard data={dataStatitics} />}
    </div>
  );
});

export const Statistics = {
  MobileWrapper: WrapperMobile,
  Records: RecordCards,
  Graphics: GraphicCard,
};

RecordCards.displayName = 'RecordCards';
GraphicCard.displayName = 'GraphicCard';
WrapperMobile.displayName = 'Statistics';
